/**
 * 借贷账户「结算」表单的**纯判定**（`DR-047` · 批 82）
 *
 * 依据：`Q域PRD` `AC-Q2.4` / `Q2.5` / `Q2.6` · `AC-Q4.6`（色彩不靠端侧猜）·
 * `DR-047` D3（三条动作的腿）· D7（超额允许 + 核销二次确认）
 *
 * ## 🔴 本文件只做"能不能提交 / 该显示什么提示"，不做算术
 *
 * - 金额文本 → 整数分：走共享内核 `yuanTextToCents()`（禁端侧除法）；
 * - 超额判定：拿**服务端下发的余额**比，不自己重算余额；
 * - 方向与色彩：**不问本文件**（服务端给 `direction`，`Amount` 给颜色）。
 *
 * ## 为什么动作要按 `contraKind` 收窄
 *
 * 「收回」只对**应收**说得通，「还款」只对**应付**说得通。让用户在一个应收账户上看到「还款」，
 * 他会以为自己欠了钱。⇒ {@link settleActionsFor} 一处给出可选项，页面不写第二份判断。
 */

import {
  SettlementType,
  SETTLEMENT_TYPE_LABEL,
  currencySymbolOf,
  ContraKind,
  formatMoneyText,
  yuanTextToCents,
  type Cents,
  type SettleContraRequest,
} from '@jiaying/shared';

/** 动作的中文名（**转发共享层**，端侧不维护第二张表） */
export function settleLabel(type: SettlementType): string {
  return SETTLEMENT_TYPE_LABEL[type];
}

/**
 * 文本金额 → 整数分；空 / 非法 ⇒ `null`。
 *
 * 🔴 `yuanTextToCents()` 对**非法文本抛 RangeError**（内核防御），不能直接抛给用户 ——
 * 这里"先判后转"（与 `account-draft.ts` 的同名局部助手**同一模式**；真正的口径在
 * `yuanTextToCents` 里，本函数不含任何算术）。
 */
function parseAmount(text: string): number | null {
  const s = text.trim();
  // 🔴 **先判格式再转**：`yuanTextToCents` 对第 3 位小数是**舍入**（它接受 '1.005'），
  //    而表单要的是**拒绝** —— 用户填 1.234 必须被拦，不能被悄悄改成 1.23。
  //    （结算金额恒为正 ⇒ 正则不含 '-?'；负数由后面的 "须大于 0" 兜）
  if (!/^\d+(\.\d{1,2})?$/.test(s)) return null;
  try {
    return yuanTextToCents(s) as number;
  } catch {
    return null;
  }
}

/**
 * 该 `contraKind` 可用的结算动作（**顺序 = 按钮顺序**）。
 *
 * | kind | 可用动作 | 理由 |
 * |---|---|---|
 * | `receivable` / `advance` / `reimburse` | 收回 · 核销 | 都是"别人欠我"，收回现金或核销 |
 * | `payable` | 还款 · 核销 | 我欠别人 |
 * | `loan`（`DR-047` D5） | **空** | 贷款要走「还本期 / 提前还款」，否则期次状态会脱节 |
 */
export function settleActionsFor(kind: ContraKind | null): readonly SettlementType[] {
  if (kind === null) return [];
  switch (kind) {
    case ContraKind.Receivable:
    case ContraKind.Advance:
    case ContraKind.Reimburse:
      return [SettlementType.Collect, SettlementType.Waive];
    case ContraKind.Payable:
      return [SettlementType.Repay, SettlementType.Waive];
    case ContraKind.Loan:
      return [];
    default:
      return [];
  }
}

/** 结算表单草稿（全文本 —— 与 `account-draft` 同一取向：端侧不存数字态） */
export interface SettleDraft {
  type: SettlementType | '';
  /** 金额（元，文本） */
  amountText: string;
  /** 日期（`YYYY-MM-DD`；默认空，由页面填今天） */
  dateText: string;
  /** 收 / 付款账户 id；`''` = 未选 */
  counterAccountId: string;
  note: string;
}

/** 新草稿：默认动作 = 该 kind 的第一个可用动作（应收 ⇒ 收回；应付 ⇒ 还款） */
export function emptySettleDraft(kind: ContraKind | null, today: string): SettleDraft {
  const actions = settleActionsFor(kind);
  return {
    type: actions[0] ?? '',
    amountText: '',
    dateText: today,
    counterAccountId: '',
    note: '',
  };
}

export type SettleBuildResult =
  | { ok: true; request: SettleContraRequest }
  | { ok: false; errors: readonly string[] };

/**
 * 草稿 → 请求体。
 *
 * 🔴 **核销不需要收付款账户**（`DR-047` D3 单腿）—— 这里显式把它置 `null`，
 * 而不是"让服务端去忽略"：契约层的 `superRefine` 会因"核销带了账户"报 400，
 * 端侧先清掉才不会把用户的输入变成一次必然失败的提交。
 */
export function buildSettleRequest(draft: SettleDraft): SettleBuildResult {
  const errors: string[] = [];
  if (draft.type === '') errors.push('请选择动作（收回 / 还款 / 核销）');

  const amount = parseAmount(draft.amountText);
  if (amount === null) errors.push('金额须为大于 0 的数字，最多两位小数');
  else if ((amount as number) <= 0) errors.push('金额须大于 0');

  if (!/^\d{4}-\d{2}-\d{2}$/.test(draft.dateText.trim())) errors.push('日期格式须为 YYYY-MM-DD');

  const needsCounter = draft.type !== '' && draft.type !== SettlementType.Waive;
  const counterId = draft.counterAccountId.trim();
  if (needsCounter && counterId === '') errors.push('请选择收付款账户');

  if (errors.length > 0 || draft.type === '' || amount === null) return { ok: false, errors };

  return {
    ok: true,
    request: {
      type: draft.type,
      amount: amount as number,
      date: draft.dateText.trim(),
      // 🔴 核销 ⇒ 显式 `null`（不是省略：省略会让 PATCH 语义式的"不传"与"不指定"混淆）
      counterAccountId: needsCounter ? counterId : null,
      note: draft.note.trim() === '' ? null : draft.note.trim(),
    },
  };
}

/**
 * 超额提示（`AC-Q2.6`：「允许（余额反向）、**不截断**、给出提示」）。
 *
 * 返回 `null` = 不需要提示（未填 / 不超额 / 余额为 0 时也无从超额）。
 * 🔴 这里只是**提示**，不阻断提交 —— 服务端也只回旗标不报错。
 */
export function overSettleHint(amountText: string, balanceCents: number, currency: string): string | null {
  const amount = parseAmount(amountText);
  if (amount === null) return null;
  const abs = Math.abs(balanceCents);
  if (abs === 0 || (amount as number) <= abs) return null;
  const over = (amount as number) - abs;
  return `超过未结余额 ${formatMoneyText(over as Cents, currency)}，结算后余额会反向（变成对方欠你 / 你欠对方）；本系统不截断、照实记。`;
}

/**
 * 核销的二次确认文案（`AC-Q2.5`：「**仅在"确定永不再报 / 不再还"时可用**」）。
 *
 * 这是**意图判定**，服务端判不了 ⇒ 端侧按钮变「确认核销」（与账户删除同范式：
 * 不用 `window.confirm`，破坏性动作要求第二次点击）。
 */
export const WAIVE_CONFIRM_HINT =
  '核销会让这笔往来直接归零（不产生资金流动、不计收支）。只有在「确定对方永远不会再还 / 公司确定不会再报」时才用；只是想暂时隐藏请用核销以外的办法。';

/** 结算动作是否需要选收付款账户（页面据此显示那个下拉） */
export function needsCounterAccount(type: SettlementType | ''): boolean {
  return type !== '' && type !== SettlementType.Waive;
}

/**
 * 金额输入框的币种提示（`AC-Q4.5` 卡面已显示币种；表单里再提示一次，防"以为对方是美元"）。
 */
export function amountFieldLabel(currency: string): string {
  return `金额（${currencySymbolOf(currency)}）`;
}

